/**
 * Reading the opening times that follow the sun.
 *
 * A venue can say it opens at sunrise, or half an hour before it. Those are
 * stored in the same `open`/`close` field as a clock time, as `"sunrise"` or
 * `"sunrise-30"` — see the Website's `features/adventures/types/solarHours.ts`,
 * which is where the format is defined and where the astronomy lives.
 *
 * This is deliberately the label half only. A moderator is comparing what a
 * venue says now with what somebody proposes it should say; "30 min before
 * sunrise" is the whole of what they need, and working out that it is 5:42 on
 * a Tuesday would need the adventure's coordinates, which the moderation row
 * does not carry.
 */

const EVENT_LABELS: Record<string, string> = {
  firstlight: 'first light',
  dawn: 'dawn',
  sunrise: 'sunrise',
  solarnoon: 'solar noon',
  sunset: 'sunset',
  dusk: 'dusk',
  lastlight: 'last light',
  fulldark: 'full dark',
}

const PATTERN = /^([a-z_]+)\s*([+-]\s*\d{1,4})?$/i

/** "90" → "1 hr 30 min". */
function formatOffset(minutes: number): string {
  const total = Math.abs(Math.trunc(minutes))
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (!hours) return `${rest} min`
  if (!rest) return `${hours} hr`
  return `${hours} hr ${rest} min`
}

/**
 * "sunset+30" → "30 min after sunset", or null if it is not a solar time.
 *
 * Null rather than a throw for anything unrecognised, so the caller falls
 * through to showing the raw value. A word added to the vocabulary after this
 * build shipped should still reach the moderator, unprettified, rather than
 * disappearing from a comparison they are about to approve.
 */
export function formatSolarTime(value?: string | null): string | null {
  if (!value) return null
  const match = PATTERN.exec(value.trim())
  if (!match) return null

  const label = EVENT_LABELS[match[1].replace(/[\s_-]/g, '').toLowerCase()]
  if (!label) return null

  const offset = match[2] ? Number(match[2].replace(/\s+/g, '')) : 0
  if (!offset || !Number.isFinite(offset)) {
    return label.charAt(0).toUpperCase() + label.slice(1)
  }
  return `${formatOffset(offset)} ${offset < 0 ? 'before' : 'after'} ${label}`
}

/** A stored time as a moderator should read it, solar or clock. */
export function describeTime(value?: string | null): string {
  if (!value) return '?'
  return formatSolarTime(value) ?? value
}
